import { FC } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { BudgetStatus } from '@/types/admin';
import ChartCard from './ChartCard';
import { useThemeColors } from './useThemeColors';

interface ForecastPoint {
  date: string;
  cumulative: number | null; // custo acumulado real (até hoje)
  projected: number | null; // projeção (de hoje até o fim do mês)
}

interface ForecastChartCardProps {
  title: string;
  subtitle?: string;
  data: ForecastPoint[];
  budget?: number | null;
  budgetStatus?: BudgetStatus;
  height?: number;
}

/**
 * Custo acumulado do mês (linha cheia) + projeção até o fim do mês (linha
 * tracejada). A cor da projeção segue o status de orçamento (ok/warning/danger).
 * `LineChartCard` só desenha 1 série — por isso este componente dedicado.
 */
const ForecastChartCard: FC<ForecastChartCardProps> = ({
  title,
  subtitle,
  data,
  budget,
  budgetStatus,
  height,
}) => {
  const colors = useThemeColors();

  if (!data || data.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height}>
        <div className="w-full h-full flex items-center justify-center text-content-muted text-sm">
          Sem dados no mês
        </div>
      </ChartCard>
    );
  }

  const projectionColor =
    budgetStatus === 'danger'
      ? colors.danger
      : budgetStatus === 'warning'
        ? colors.warning
        : colors.accent;

  return (
    <ChartCard title={title} subtitle={subtitle} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            stroke={colors.textMuted}
            tick={{ fill: colors.textMuted, fontSize: 13 }}
            tickFormatter={formatXTick}
          />
          <YAxis
            stroke={colors.textMuted}
            tick={{ fill: colors.textMuted, fontSize: 13 }}
            label={{
              value: 'USD',
              angle: -90,
              position: 'insideLeft',
              fill: colors.textMuted,
              fontSize: 13,
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              color: colors.text,
              fontSize: 14,
            }}
            labelStyle={{ color: colors.textMuted }}
          />
          {budget ? (
            <ReferenceLine
              y={budget}
              stroke={colors.danger}
              strokeDasharray="4 4"
              label={{ value: 'Meta', fill: colors.textMuted, fontSize: 13, position: 'insideTopRight' }}
            />
          ) : null}
          <Line
            type="monotone"
            dataKey="cumulative"
            name="Acumulado"
            stroke={colors.accent}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="projected"
            name="Projeção"
            stroke={projectionColor}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

function formatXTick(value: string | number): string {
  if (typeof value !== 'string') return String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return `${m[3]}/${m[2]}`;
  return value;
}

export default ForecastChartCard;
